import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import PortraitIcon from '@mui/icons-material/Portrait';
import SegmentIcon from '@mui/icons-material/Segment';
import SellIcon from '@mui/icons-material/Sell';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';

import PresentationStepper from "./PresentationStepper";
import ContentForm from "./ContentForm"; // Import the ContentForm component
import CustomerSegments from "./CustomerSegments";
import GeneratePresentation from "./GeneratePresentation";

import { fetchProducts } from "../../actions/productActions";
import { fetchValueModels } from "../../actions/valueActions";
import { addPresentation, fetchPresentations, editPresentation } from "../../actions/presentationAction";
import { compareKeys } from "../helpers/compareFunctions";
import history from "../../history";

const steps = ['Content', 'Customer segments', 'Generate Presentation'];
const slides = [
    {
        id: '1',
        slide: 'Value by Customer segment',
        icon: <SegmentIcon fontSize="inherit" />
    },
    {
        id: '2',
        slide: 'Value for each Customer segment',
        icon: <WaterfallChartIcon fontSize="inherit" />
    }
];


// function getStepContent(step) {
//     switch (step) {
//         case 0:
//             // When activeStep is 0, ContentForm will be rendered in the component below.
//             return null;
//         case 1:
//             return <Box>Step 1 content goes here</Box>;
//         case 2:
//             return <Box>Step 2 content goes here</Box>;
//         default:
//             return null;
//     }
// }

const ChosenSlidesList = ({ chosenSlides }) => {
    if (!chosenSlides) {
        return null;
    }

    // Sort chosen slides based on the id from the slides array.
    const sortedSlides = [...chosenSlides].sort((a, b) => {
        const aData = slides.find(item => item.id === a);
        const bData = slides.find(item => item.id === b);
        return (aData?.id || 0) - (bData?.id || 0);
    });

    return (
        <Stack spacing={1}>
            {sortedSlides.map((slideId, index) => {
                const slideData = slides.find(item => item.id === slideId);
                return (
                    <Typography
                        key={index}
                        variant="caption"
                        display="flex"
                        alignItems="center"
                        sx={{ color: 'white' }}
                    >
                        {slideData && slideData.icon}
                        <span style={{ marginLeft: 4 }}>{slideData.slide}</span>
                    </Typography>
                );
            })}
        </Stack>
    );
};

const CustomerSegmentsList = ({ customerSegments }) => {
    if (!customerSegments) {
        return null;
    }

    return (
        <Stack spacing={1}>
            {customerSegments.map((segment, index) => {
                return (
                    <Typography
                        key={index}
                        variant="caption"
                        display="flex"
                        alignItems="center"
                        sx={{ color: 'white' }}
                    >
                        <PortraitIcon fontSize="inherit" />
                        <span style={{ marginLeft: 4 }}>{segment.name}</span>
                    </Typography>
                );
            })}
        </Stack>
    );
};

const Presentations = () => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const [activeStep, setActiveStep] = useState(0);

    // Access state for presentations
    const id = searchParams.get('id');
    const products = useSelector(state => state.product.products);
    const product = Object.values(products).find(product => product.id === parseInt(id));
    const valueModels = useSelector(state => state.value.valueModels);
    const presentations = useSelector(state => state.presentation) || {};
    const presentation = Object.values(presentations).find(presentation => presentation.product.id === parseInt(id)) || {};
    const filteredValueModels = valueModels.filter(valueModel =>
        valueModel.product?.id === parseInt(id) && valueModel.step === 'completed'
    );


    useEffect(() => {
        dispatch(fetchProducts());
        dispatch(fetchValueModels());
        dispatch(fetchPresentations());
        // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, []);

    // This function is called when ContentForm is submitted (activeStep === 0)
    const handleContentSubmit = (values) => {
        values.product = product;

        // If no presentation object is present create one
        if (Object.keys(presentation).length === 0) {
            dispatch(addPresentation(values));
        }
        // Dispatch an update if any of the keys are changed
        else if (!compareKeys(values, presentation, ['title', 'slides'])) {
            dispatch(editPresentation(presentation.id, values))
        }
        // Move to the next step after successful submission
        setActiveStep(1);
    };

    // Modified handleNext: if activeStep === 0, trigger form submission instead.
    const handleNext = () => {
        if (activeStep === 0) {
            // Trigger form submission via the ref
            if (formikRef.current) {
                formikRef.current.handleSubmit();
            }
        } else {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 5, mt: 3 }}>
                    <AutoGraphIcon />
                    <Typography variant='h6'>
                        Generate Presentation
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIosIcon />}
                    onClick={() => history.push('/')}
                    sx={{ mr: '30%', mt: 3, textTransform: 'none' }}
                >
                    Back
                </Button>
            </Stack>
            <Box>
                <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 5, mt: 3, display: 'inline-flex', border: '1px solid #ccc', borderRadius: '8px', padding: 2 }}>
                    <SellIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Product:
                    </Typography>
                    <Typography variant="body2">
                        {!product ? 'No Product' : product.name}
                    </Typography>
                </Stack>
            </Box>
            <Box sx={{ ml: 5, mt: 5, mb: 5, width: '70%' }}>
                <Card sx={{ height: 500, display: 'flex' }}>
                    <Grid container sx={{ height: '100%' }}>
                        {/* Left Area: occupies 40% of the width, hidden on xs screens */}
                        <Grid
                            item
                            xs={12}
                            md={5}
                            lg={4}
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                flexDirection: 'column',
                                background: theme =>
                                    `linear-gradient(to bottom right, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                height: '100%',
                                flexGrow: 1,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    color: 'white',
                                    p: 1,
                                }}
                            >
                                <Grid container spacing={2}>
                                    {/* Header */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6">Presentation</Typography>
                                    </Grid>
                                    {/* Title Row */}
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2" fontWeight="bold">
                                            Title:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9}>
                                        <Typography variant="body2">
                                            {presentation?.title || 'No title provided'}
                                        </Typography>
                                    </Grid>
                                    {/* Content Row */}
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2" fontWeight="bold">
                                            Content:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9}>
                                        <ChosenSlidesList chosenSlides={presentation?.slides || []} />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2" fontWeight="bold">
                                            Customer segments:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9}>
                                        <CustomerSegmentsList customerSegments={presentation?.customer_segments} />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        {/* Vertical Divider: visible on md and larger */}
                        <Grid
                            item
                            md={0.5}
                            sx={{
                                display: { xs: 'none', md: 'block' },
                            }}
                        >
                            <Divider orientation="vertical" flexItem />
                        </Grid>
                        {/* Right Area: occupies the remaining space */}
                        <Grid
                            item
                            xs={12}
                            md={6.5}
                            lg={7.5}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                p: 2,
                                justifyContent: 'space-between',
                            }}
                        >
                            <PresentationStepper
                                steps={steps}
                                activeStep={activeStep}
                            />
                            {activeStep === 0 &&
                                // Render the ContentForm when activeStep is 0
                                <ContentForm
                                    ref={formikRef}
                                    slides={slides}
                                    presentation={presentation}
                                    onSubmit={handleContentSubmit}
                                />
                            }
                            {activeStep === 1 &&
                                <CustomerSegments
                                    valueModels={filteredValueModels}
                                    presentation={presentation}
                                />
                            }
                            {activeStep === 2 &&
                                <GeneratePresentation
                                    presentation={presentation}
                                />
                            }
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Button
                                    startIcon={<ChevronLeftRoundedIcon />}
                                    onClick={handleBack}
                                    disabled={activeStep === 0}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Back
                                </Button>
                                {activeStep < 2 &&
                                    <Button
                                        onClick={() => {
                                            if (activeStep === 0 && formikRef.current) {
                                                formikRef.current.handleSubmit();
                                            } else {
                                                handleNext();
                                            }
                                        }}
                                        disabled={activeStep === 1 && ((presentation?.customer_segments?.length ?? 0) === 0)}
                                        endIcon={<ChevronRightRoundedIcon />}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                    </Button>
                                }
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
            </Box>
        </Box>
    );
};

export default Presentations;
